import { useSyncExternalStore } from "react";

type MutationState = { pending: boolean; error: string };
const idle: MutationState = { pending: false, error: "" };
const states = new Map<string, MutationState>();
export function projectMutationPending(
  scope: string,
  projectId: string,
): boolean {
  return states.get(`${scope}:${projectId}`)?.pending === true;
}
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
function publish(key: string, state: MutationState) {
  states.set(key, state);
  for (const listener of listeners) listener();
}

/** Mutation locks outlive route unmounts. Never retry a possibly completed write. */
export function useProjectMutation(scope: string, projectId: string) {
  const key = `${scope}:${projectId}`;
  const state = useSyncExternalStore(subscribe, () => states.get(key) || idle);
  const run = async (action: () => Promise<void>): Promise<boolean> => {
    if (states.get(key)?.pending) return false;
    publish(key, { pending: true, error: "" });
    try {
      await action();
      publish(key, idle);
      return true;
    } catch (error) {
      publish(key, {
        pending: false,
        error:
          error instanceof Error
            ? error.message
            : "This change could not be saved. Review before retrying.",
      });
      return false;
    }
  };
  return { ...state, run };
}
